/**
 * CaseOutreachHistory
 *
 * Admin-only panel on a case-detail page showing every outreach letter that
 * references this case (via `outreach_logs.case_ids`). Displays status, sent
 * date, agency, subject, and a link to the full letter in /admin/outreach.
 *
 * Stays hidden if no letters reference the case (quiet by design).
 */
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Mail, Clock, Send, CheckCheck, ExternalLink } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_META = {
  pending_approval:                  { label: 'Pending Approval',   color: 'bg-amber-100 text-amber-800 border-amber-300',   icon: Clock },
  pending_approval_phone_required:   { label: 'Phone Required',     color: 'bg-purple-100 text-purple-800 border-purple-300', icon: Clock },
  pending_approval_mail_required:    { label: 'Mail Required (FOIA)', color: 'bg-slate-200 text-slate-800 border-slate-400', icon: Clock },
  sent:                              { label: 'Sent',               color: 'bg-blue-100 text-blue-800 border-blue-300',       icon: Send },
  responded:                         { label: 'Responded',          color: 'bg-emerald-100 text-emerald-800 border-emerald-300', icon: CheckCheck },
  cancelled:                         { label: 'Cancelled',          color: 'bg-slate-100 text-slate-600 border-slate-300',    icon: Clock },
};

const fmtDate = (iso) => {
  if (!iso) return null;
  try { return new Date(iso).toLocaleDateString('en-US',
    { year: 'numeric', month: 'short', day: 'numeric' }); }
  catch { return iso; }
};

export default function CaseOutreachHistory({ caseId }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!caseId) return;
    let alive = true;
    (async () => {
      try {
        const r = await axios.get(
          `${API}/admin/outreach/by-case/${caseId}`,
          { withCredentials: true });
        if (alive) setRows(r.data?.outreach || []);
      } catch (_err) {
        // Silent: endpoint is admin-only; non-admins get 401/403.
        if (alive) setRows([]);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [caseId]);

  if (loading) return null;
  if (!rows.length) return null;

  return (
    <Card data-testid="case-outreach-history" className="border-slate-200">
      <CardHeader className="pb-3">
        <CardTitle className="font-heading flex items-center gap-2 text-base">
          <Mail className="w-4 h-4 text-rose-600" />
          Agency Outreach ({rows.length})
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0 space-y-3">
        {rows.map((r) => {
          const meta = STATUS_META[r.status] || { label: r.status, color: 'bg-slate-100 border-slate-300', icon: Clock };
          const Icon = meta.icon;
          const sentDate = fmtDate(r.sent_at);
          const respondedDate = fmtDate(r.response_at);
          return (
            <div
              key={r.id}
              data-testid={`case-outreach-item-${r.id}`}
              className="border rounded-lg p-3 hover:bg-slate-50 transition-colors">
              <div className="flex items-start justify-between gap-3 mb-2 flex-wrap">
                <div className="min-w-0 flex-1">
                  <div className="font-medium text-sm leading-tight">
                    {r.subject || '(no subject)'}
                  </div>
                  <div className="text-xs text-slate-600 mt-0.5">
                    {r.agency_name}
                    {r.recipient_name && r.recipient_name !== r.agency_name &&
                      <span className="text-slate-500"> — {r.recipient_name}</span>}
                  </div>
                </div>
                <Badge variant="outline" className={`${meta.color} flex items-center gap-1 shrink-0 text-xs`}>
                  <Icon className="w-3 h-3" />
                  {meta.label}
                </Badge>
              </div>
              <div className="flex items-center gap-3 text-xs text-slate-500 flex-wrap">
                {sentDate && (
                  <span className="flex items-center gap-1">
                    <Send className="w-3 h-3" /> Sent {sentDate}
                  </span>
                )}
                {respondedDate && (
                  <span className="flex items-center gap-1 text-emerald-700">
                    <CheckCheck className="w-3 h-3" /> Replied {respondedDate}
                  </span>
                )}
                {!sentDate && r.created_at && (
                  <span className="flex items-center gap-1">
                    <Clock className="w-3 h-3" /> Drafted {fmtDate(r.created_at)}
                  </span>
                )}
                {r.variant && (
                  <span className="text-slate-400">variant: {r.variant}</span>
                )}
                <Link
                  to={`/admin/outreach?open=${r.id}`}
                  className="ml-auto inline-flex items-center gap-1 text-rose-700 hover:text-rose-900"
                  data-testid={`case-outreach-open-${r.id}`}>
                  Open letter <ExternalLink className="w-3 h-3" />
                </Link>
              </div>
            </div>
          );
        })}
        <div className="text-xs text-slate-400 pt-1">
          Full queue: <Link to="/admin/outreach" className="underline hover:text-slate-600">/admin/outreach</Link>
        </div>
      </CardContent>
    </Card>
  );
}
